import { TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { itemsService } from "../../services/items.service";
import { Accordion, AccordionSummary, AccordionDetails, Typography, CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatCustomNumber } from "../../helpers/formatCustomNumber";
import { Table } from "@mui/material"; // Fixed to use MUI Table for consistency

function ItemStockInformation({ Item_Id }) {
  const [stockList, setStockList] = useState([]);
  const [viewMode, setViewMode] = useState("accounting"); 
  const [expanded, setExpanded] = useState(false); // Track open state
  const [loading, setLoading] = useState(false);
  const [lastLoadedId, setLastLoadedId] = useState(null);
  


  useEffect(() => {
    setStockList([]);
    setLastLoadedId(null);
    // Optional: setExpanded(false) if you want it to close on ID change
  }, [Item_Id]);

  // 1. Fetch only when expanded AND Item_Id exists AND data is empty
  useEffect(() => {
    // Fetch if: It's open AND we have an ID AND it's not the one we already loaded
    if (expanded && Item_Id && Item_Id !== lastLoadedId) {
      fetchStockInformation();
    }
  }, [expanded, Item_Id, lastLoadedId]);

  const fetchStockInformation = async () => {
    setLoading(true);
    try {
      const res = await itemsService.getItemStockInformation(Item_Id);
      if (res?.data) {
        setStockList(res.data);
        setLastLoadedId(Item_Id); // Mark this ID as loaded
      }
    } catch (error) {
      console.error("Error fetching stock info:", error);
    } finally {
      setLoading(false);
    }
  };

  // Logic to handle Accordion toggle
  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };

  const totals = useMemo(() => {
    return stockList.reduce((acc, item) => {
      acc.onHand += item.Stock_In_Hand || 0;
      acc.committed += item.Commited_Stock || 0;
      acc.available += item.Available_For_Sale || 0;
      acc.price += item.Stock_Price || 0;
      return acc;
    }, { onHand: 0, committed: 0, available: 0, price: 0 });
  }, [stockList]);

  const activeBtn = "py-2 px-4 text-sm font-bold rounded-md bg-blue-600 text-white shadow-md transition-all";
  const inactiveBtn = "py-2 px-4 text-sm font-medium rounded-md bg-transparent text-gray-500 hover:text-blue-600 transition-all";

  return (
    <div className="w-full shadow-sm rounded-lg overflow-hidden border border-gray-200 bg-white">
      <Accordion 
        elevation={0} 
        expanded={expanded} 
        onChange={handleAccordionChange}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className="bg-gray-50">
          <div className="flex items-center gap-3">
            <Typography className="font-bold text-gray-700">Stock Details</Typography>
            {loading && <CircularProgress size={20} />}
          </div>
        </AccordionSummary>
        
        <AccordionDetails>
          <div className="flex flex-col gap-4">
            {/* TOGGLE SYSTEM */}
            <div className="flex items-center justify-between border-b pb-4">
              <div className="flex bg-gray-100 p-1 rounded-lg">
                <button 
                  onClick={() => setViewMode("accounting")} 
                  className={viewMode === "accounting" ? activeBtn : inactiveBtn}
                >
                  Accounting Stock
                </button>
                <button 
                  onClick={() => setViewMode("physical")} 
                  className={viewMode === "physical" ? activeBtn : inactiveBtn}
                >
                  Physical Stock
                </button>
              </div>
              <div className="text-right">
                <span className="text-xs font-bold text-gray-400 uppercase">Current View</span>
                <p className="text-sm font-semibold text-blue-600 uppercase">{viewMode}</p>
              </div>
            </div>

            <TableContainer component={Paper} variant="outlined" className="max-h-[400px]">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className="font-bold bg-gray-50">Warehouse</TableCell>
                    <TableCell align="right" className="font-bold bg-gray-50">Stock In Hand</TableCell>
                    {viewMode === "accounting" && (
                      <>
                        <TableCell align="right" className="font-bold bg-gray-50">Committed</TableCell>
                        <TableCell align="right" className="font-bold bg-gray-50">Available</TableCell>
                      </>
                    )}
                    <TableCell align="right" className="font-bold bg-gray-50">Stock Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockList.length > 0 ? (
                    stockList.map((stock, i) => (
                      <TableRow key={i} hover>
                        <TableCell className="font-medium">{stock.Warehouse_Name}</TableCell>
                        <TableCell align="right">{formatCustomNumber(stock.Stock_In_Hand)}</TableCell>
                        {viewMode === "accounting" && (
                          <>
                            <TableCell align="right" className="text-red-500">-{formatCustomNumber(stock.Commited_Stock)}</TableCell>
                            <TableCell align="right" className="font-bold text-green-600 bg-green-50">
                              {formatCustomNumber(stock.Available_For_Sale)}
                            </TableCell>
                          </>
                        )}
                        <TableCell align="right">{formatCustomNumber(stock.Stock_Price)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" className="py-8 text-gray-400 italic">
                        {loading ? "Loading stock information..." : "No stock data available"}
                      </TableCell>
                    </TableRow>
                  )}

                  {stockList.length > 0 && (
                    <TableRow className="bg-gray-100">
                      <TableCell className="font-bold">TOTAL</TableCell>
                      <TableCell align="right" className="font-bold">{formatCustomNumber(totals.onHand)}</TableCell>
                      {viewMode === "accounting" && (
                        <>
                          <TableCell align="right" className="font-bold text-red-500">-{formatCustomNumber(totals.committed)}</TableCell>
                          <TableCell align="right" className="font-bold text-green-700">{formatCustomNumber(totals.available)}</TableCell>
                        </>
                      )}
                      <TableCell align="right" className="font-bold">{formatCustomNumber(totals.price)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

export default ItemStockInformation;